'use es6';

import { combineReducers } from 'redux';

// Reducers from CEUI   ---------------------------------------------------------------------

// App
import appStatusReducer from 'ContentEditorUI/redux/reducers/appStatusReducer';
import authReducer from 'ContentEditorUI/redux/reducers/authReducer';
import contentSchemaReducer from 'ContentEditorUI/redux/reducers/contentSchemaReducer';
import panesVisitStatusReducer from 'ContentEditorUI/redux/reducers/panesVisitStatusReducer';
import portalReducer from 'ContentEditorUI/redux/reducers/portalReducer';
import usersReducer from 'ContentEditorUI/js/redux/reducers/usersReducer';
import userAttributesReducer from 'ContentEditorUI/redux/reducers/userAttributesReducer';
import locallyCachedSettingsReducer from 'ContentEditorUI/redux/reducers/locallyCachedSettingsReducer';
import { sidebarStateReducer } from 'ContentEditorUI/redux/reducers/sidebarStateReducer';
import selectionReducer from 'ContentEditorUI/redux/reducers/selectionReducer';
import iframesReducer from 'ContentEditorUI/redux/reducers/iframesReducer';
import brandSettingsReducer from 'ContentEditorUI/redux/reducers/brandSettingsReducer';
import { categorizationReducer } from 'ContentEditorUI/redux/reducers/categorizationReducer';
import modalReducer from 'ContentEditorUI/redux/reducers/modalReducer';
import sectionsReducer from 'ContentEditorUI/redux/reducers/sectionsReducer';
import createCollaborationSidebarReducer from 'ContentEditorUI/redux/reducers/createCollaborationSidebarReducer';
import locationReducer from 'ContentEditorUI/redux/reducers/locationReducer';
import cmsSettingsReducer from 'ContentEditorUI/redux/reducers/cmsSettingsReducer';

// Modules
import justSavedModuleReducer from 'ContentEditorUI/redux/reducers/justSavedModuleReducer';
import editSidebarReducer from 'ContentEditorUI/redux/reducers/editSidebarReducer';
import moduleSchemaReducer from 'ContentEditorUI/redux/reducers/moduleSchemaReducer';
import moduleReducer, { compareModuleReducerSlices, moduleReducerAsJSObject } from 'ContentEditorUI/redux/reducers/moduleReducer';
import { enableModuleReducerDiffing } from 'ContentEditorUI/utils/moduleReducerUtils';
import createUndoableModuleReducer, { createUndoableModuleReducerAsPOJO } from 'ContentEditorUI/redux/reducers/undoableModuleReducer';
import moduleErrorReducer from 'ContentEditorUI/redux/reducers/moduleErrorReducer';
import deleteRequestReducer from 'ContentEditorUI/redux/reducers/deleteRequestReducer';
import cloneRequestReducer from 'ContentEditorUI/redux/reducers/cloneRequestReducer';
import moduleVersionsReducer from 'ContentEditorUI/redux/reducers/moduleVersionsReducer';

// Content
import resources from 'ContentData/reducers/Resources';
import editingInfoReducer from 'ContentEditorUI/redux/reducers/editingInfoReducer';
import saveDataReducer from 'ContentEditorUI/redux/reducers/saveDataReducer';
import formStatusReducer from 'ContentEditorUI/redux/reducers/formStatusReducer';
import pagesReducer from 'ContentEditorUI/redux/reducers/pagesReducer';
import abTestReducer from 'ContentEditorUI/redux/reducers/abTestReducer';
import accountVerificationStatusReducer from 'ContentEditorUI/redux/reducers/accountVerificationStatusReducer';
import contentReadOnlyDataReducer from 'ContentEditorUI/redux/reducers/contentReadOnlyDataReducer';
import layoutDataReducer from 'ContentEditorUI/redux/reducers/layoutDataReducer';
import languageReducer from 'ContentEditorUI/redux/reducers/languageReducer';
import mabExperimentReducer from 'page-editor-ui/redux/reducers/mabExperimentReducer';
import revisionsReducer from 'ContentEditorUI/redux/reducers/revisionsReducer';
import hardSavedContentModelReducer from 'ContentEditorUI/redux/reducers/contentModel/hardSavedContentModelReducer';
import styleBreakpointsReducer from 'ContentEditorUI/redux/reducers/styleBreakpointsReducer';
import contentAssetsReducer from 'ContentEditorUI/redux/reducers/contentAssetsReducer';
import optimizationsReducer from 'ContentEditorUI/redux/reducers/optimizationsReducer';
import inpageOptimizationsReducer from 'ContentEditorUI/redux/reducers/inpageOptimizationReducer';
import headerReducer from 'ContentEditorUI/redux/reducers/headerReducer';

// Smart View
import smartViewReducer from 'ContentEditorUI/redux/reducers/smartViewReducer';

// Inpage Editor
import inpageReducer from 'ContentEditorUI/redux/reducers/inpageReducer';
import inpageModals from 'tinymce-plugins/reducers/InpageModals';
import siteSettingsReducer from 'ContentEditorUI/redux/reducers/siteSettingsReducer';
import portalDomainsReducer from 'ContentEditorUI/redux/reducers/portalDomainsReducer';
import templateInfoReducer from 'ContentEditorUI/redux/reducers/templateInfoReducer';
import themeReducer from 'ContentEditorUI/redux/reducers/themeReducer';
import uploadedFeaturedImagesReducer from 'ContentEditorUI/redux/reducers/uploadedFeaturedImagesReducer';
import testFlagsReducer from 'ContentEditorUI/redux/reducers/testFlagsReducer';

// Theme Editor
import themeSettingsReducer, { themeSettingsAndBreakpointStyles } from 'theme-previewer-lib/reducers/themeSettingsReducer';
import themeSettingsMetaReducer from 'theme-previewer-lib/reducers/themeSettingsMetaReducer';
import presetsReducer from 'theme-previewer-lib/reducers/presetsReducer';
import themeDependentAssetsReducer from 'theme-previewer-lib/reducers/themeDependentAssetsReducer';
import themeRevisionsReducer from 'theme-previewer-lib/reducers/revisionsReducer';
import universalThemeReducer from 'theme-previewer-lib/reducers/universalThemeReducer';

// Instance editing
import instanceEditingReducer from 'ContentEditorUI/redux/reducers/instanceEditingReducer';

// Content assistant
import contentAssistantReducer from 'ContentEditorUI/redux/reducers/contentAssistantReducer';

// Reducers from page-editor-ui ---------------------------------------------------------------------
import pageContentModelReducer, { pageContentModelReducerAsJSObject } from 'page-editor-ui/redux/reducers/pageContentModelReducer';
import pageRequestStatesReducer from 'page-editor-ui/redux/reducers/pageRequestStatesReducer';
import pageSettingsReducer from 'page-editor-ui/redux/reducers/pageSettingsReducer';
import pageLimitReducer from 'page-editor-ui/redux/reducers/pageLimitReducer';
import { extraInitialStateReducer } from 'page-editor-ui/redux/reducers/extraInitialStateReducer';
import stagedPageReducer from 'page-editor-ui/redux/reducers/stagedPageReducer';
import testVariationTourStatusReducer from './testVariationTourStatusReducer';
import immutableToImmerReducer from 'ContentEditorUI/redux/reduxReducerDiffer';
import { runDiffsOnFiftyPercentOfAppLoads, conditionallyRunDiffsBasedOnPercentage } from 'ContentEditorUI/redux/reducers/reduxReducerDifferUtils';
import { gate } from 'hub-http/gates';
import reduceReducers from 'ContentEditorUI/utils/reduceReducers';
import { produce } from 'immer';
export const runDiffsOnTenPercentOfAppLoads = conditionallyRunDiffsBasedOnPercentage(0.1);
enableModuleReducerDiffing();
const updateStateSlice = (key, reducer) => (state, action) => {
  return state.update(key, sliceState => {
    const result = reducer(sliceState, action);
    return result;
  });
};

//supporting undo/redo of theme settings in editor
const diffedUndoableModuleReducer = immutableToImmerReducer(createUndoableModuleReducer(reduceReducers(moduleReducer, updateStateSlice('themeSettings', themeSettingsAndBreakpointStyles))), createUndoableModuleReducerAsPOJO(reduceReducers(moduleReducerAsJSObject, (state, action) => {
  return produce(state, draft => {
    const values = state.themeSettings;
    draft.themeSettings = themeSettingsAndBreakpointStyles(values, action);
  });
})), {
  reducerName: 'modules',
  reducerToReturn: 'immer',
  prodConfig: {
    computeDiffOnProd: 'conditionally',
    condition: runDiffsOnTenPercentOfAppLoads
  },
  trackActions: true,
  serializeImmutableState: immutableState => immutableState.get('present').toJS(),
  computeStateDiff: (immutablePresentState, immerUndoableState) => compareModuleReducerSlices(immutablePresentState, immerUndoableState.present)
});
export default combineReducers({
  // Reducers from CEUI
  abTest: abTestReducer,
  appStatus: appStatusReducer,
  accountVerificationStatus: accountVerificationStatusReducer,
  auth: authReducer,
  brandSettings: brandSettingsReducer,
  categorizations: categorizationReducer,
  collaborationSidebar: createCollaborationSidebarReducer(),
  contentReadOnlyData: contentReadOnlyDataReducer,
  contentAssets: contentAssetsReducer,
  moduleErrors: moduleErrorReducer,
  contentAssistant: contentAssistantReducer,
  contentModel: immutableToImmerReducer(pageContentModelReducer, pageContentModelReducerAsJSObject, {
    reducerName: 'contentModel',
    reducerToReturn: {
      type: 'gated',
      gateName: gate('ContentEditorUI:V3:ImmerContentModel')
    },
    prodConfig: {
      computeDiffOnProd: 'conditionally',
      condition: runDiffsOnFiftyPercentOfAppLoads
    }
  }),
  contentSchema: contentSchemaReducer,
  editSidebar: editSidebarReducer,
  panesVisitStatus: panesVisitStatusReducer,
  portalDomains: portalDomainsReducer,
  portal: portalReducer,
  editingInfo: editingInfoReducer,
  saveData: saveDataReducer,
  formStatus: formStatusReducer,
  iframes: iframesReducer,
  inpageData: inpageReducer,
  inpageModals,
  instanceEditing: instanceEditingReducer,
  styleBreakpoints: styleBreakpointsReducer,
  justSavedModule: justSavedModuleReducer,
  locallyCachedSettings: locallyCachedSettingsReducer,
  deleteRequest: deleteRequestReducer,
  cloneRequest: cloneRequestReducer,
  moduleSchemas: moduleSchemaReducer,
  modules: diffedUndoableModuleReducer,
  pages: pagesReducer,
  siteSettings: siteSettingsReducer,
  requestStates: pageRequestStatesReducer,
  revisions: revisionsReducer,
  resources,
  selection: selectionReducer,
  sidebarState: sidebarStateReducer,
  smartView: smartViewReducer,
  templateInfo: templateInfoReducer,
  testFlags: testFlagsReducer,
  theme: themeReducer,
  uploadedFeaturedImages: uploadedFeaturedImagesReducer,
  users: usersReducer,
  layoutData: layoutDataReducer,
  languages: languageReducer,
  userAttributes: userAttributesReducer,
  mabExperiment: mabExperimentReducer,
  hardSavedContentModel: hardSavedContentModelReducer,
  modals: modalReducer,
  sections: sectionsReducer,
  optimizationState: optimizationsReducer,
  inpageOptimizationState: inpageOptimizationsReducer,
  header: headerReducer,
  __location: locationReducer,
  // Reducers from theme-previewer-lib
  themeSettingsMeta: themeSettingsMetaReducer,
  presets: presetsReducer,
  themeDependentAssets: themeDependentAssetsReducer,
  themeRevisions: themeRevisionsReducer,
  themeSettings: themeSettingsReducer,
  universalTheme: universalThemeReducer,
  // Reducers from PageEditorUI
  contentTypeLimit: pageLimitReducer,
  pageSettings: pageSettingsReducer,
  extraInitialState: extraInitialStateReducer,
  stagedPage: stagedPageReducer,
  testVariationTourStatus: testVariationTourStatusReducer,
  cmsSettings: cmsSettingsReducer,
  moduleVersions: moduleVersionsReducer
});